import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { Typography, Grid } from '@mui/material_modern'
import { useGetInventoryMaterialTypeQuery } from 'services/inventory'
import { parseNumberObject } from 'utils/helpers'
import { addToTime } from 'utils/data-manipulation/common'

// materials are the regrade materials
const RegradeSummaryInformationRow = ({ material, effectiveDate, costOfMaterial, fromQuantity, tosQuantity }) => {
  const { defaultYardUm } = useSelector(state => state.settings)

  const { enableFourDecimalPrecisionInInventory } = useSelector(state => state.featureFlags?.variables || {})

  const { data: inventoryMaterialTypeData } = useGetInventoryMaterialTypeQuery(
    // use 10 milliseconds before the selected effectiveDate
    {
      materialTypeId: material?.material?.id,
      filter: JSON.stringify({ effectiveDate: addToTime(effectiveDate, -10).toISOString() }),
    },
    {
      skip: !material?.material?.id,
      refetchOnMountOrArgChange: true,
    },
  )

  const summaryInformation = useMemo(() => {
    let newWIPAmount = 0
    let newAverageCostAmount = 0
    let newAverageCostChangeAmount = 0
    let newTotalCostAmount = 0

    if (inventoryMaterialTypeData) {
      if (material.group === 'from') {
        newWIPAmount = Number(inventoryMaterialTypeData.wip?.amount) - Number(fromQuantity)
        newAverageCostAmount = parseNumberObject(inventoryMaterialTypeData.wipAvgCost)
        newAverageCostChangeAmount = 0
        newTotalCostAmount = newWIPAmount * newAverageCostAmount
      } else {
        newWIPAmount = Number(inventoryMaterialTypeData.wip?.amount) + Number(material.quantity)
        newTotalCostAmount = !tosQuantity
          ? 0
          : parseNumberObject(inventoryMaterialTypeData?.wipCost) + (material.quantity / tosQuantity) * costOfMaterial
        newAverageCostAmount = newTotalCostAmount / newWIPAmount
        newAverageCostChangeAmount = newAverageCostAmount - parseNumberObject(inventoryMaterialTypeData?.wipAvgCost)
      }
    }

    return {
      newWIP: { amount: newWIPAmount },
      newAverageCost: { amount: newAverageCostAmount },
      newAverageCostChange: { amount: newAverageCostChangeAmount },
      newTotalCost: { amount: newTotalCostAmount },
    }
  }, [inventoryMaterialTypeData, material, costOfMaterial, fromQuantity, tosQuantity])

  return (
    <Grid container columns={12} mb={2}>
      <Grid item xs={2.5}>
        <Typography variant="caption" color="textSecondary">
          Material
        </Typography>
        <Typography variant="body1" noWrap>
          {material.material?.commonName}
        </Typography>
      </Grid>
      <Grid item xs={2.5}>
        <Typography variant="caption" color="textSecondary">
          New WIP
        </Typography>
        <Typography variant="body1" noWrap>
          {parseNumberObject(summaryInformation?.newWIP, { format: 'weight', unitOverride: defaultYardUm })}
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <Typography variant="caption" color="textSecondary">
          New Average Cost
        </Typography>
        <Typography variant="body1" noWrap>
          {`${parseNumberObject(summaryInformation?.newAverageCost, {
            format: 'price',
            enableFourDecimalPrecisionInInventory,
            unitOverride: defaultYardUm,
          })} (${parseNumberObject(summaryInformation?.newAverageCostChange) >= 0 ? '+' : '-'}${parseNumberObject(
            summaryInformation?.newAverageCostChange,
            { format: 'price', enableFourDecimalPrecisionInInventory, unitOverride: defaultYardUm },
          )})`}
        </Typography>
      </Grid>
      <Grid item xs={3}>
        <Typography variant="caption" color="textSecondary">
          New Total Cost
        </Typography>
        <Typography variant="body1" noWrap>
          {parseNumberObject(summaryInformation?.newTotalCost, {
            format: 'value',
            enableFourDecimalPrecisionInInventory,
          })}
        </Typography>
      </Grid>
    </Grid>
  )
}

RegradeSummaryInformationRow.propTypes = {
  material: PropTypes.object,
  effectiveDate: PropTypes.string,
  costOfMaterial: PropTypes.number,
  fromQuantity: PropTypes.number,
  tosQuantity: PropTypes.number,
}

RegradeSummaryInformationRow.defaultProps = {
  material: {},
  effectiveDate: '',
  costOfMaterial: 0,
  fromQuantity: 0,
  tosQuantity: 0,
}

export default RegradeSummaryInformationRow
